import { AfterViewInit, Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Tab } from './tab.interface';
import { HashTriggerable } from '@finder/shared/features/hash-triggerable/hash-triggerable-base';
import { HashTriggerableService } from '@finder/shared/features/hash-triggerable/hash-triggerable.service';
import { IdGeneratorPipe } from '@finder/shared/utils/pipes/id-generator/id-generator.pipe';
import { Subject } from 'rxjs';
import { CommonModule, DOCUMENT } from '@angular/common';
import { DisclaimerComponent } from '../disclaimer/disclaimer.component';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { ButtonComponent } from '../button/button.component';
import { TabContentRendererComponent } from '../tab-content-renderer/tab-content-renderer.component';
import { PictureComponent } from '../picture/picture.component';
import { BREAKPOINTS } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CreateStyleProvider, CREATE_STYLE } from '@finder/shared/utils/dom/createStyle';

@Component({
    selector: 'finder-tab',
    templateUrl: './tab.component.html',
    styleUrls: ['./tab.component.scss'],
    standalone: true,
    imports: [
        ButtonComponent,
        CommonModule,
        ComponentHeaderComponent,
        DisclaimerComponent,
        IdGeneratorPipe,
        TabContentRendererComponent,
        PictureComponent,
    ],
})
export class TabComponent extends HashTriggerable implements OnInit, AfterViewInit {
    private createStyle: CreateStyleProvider = inject(CREATE_STYLE);
    private document: Document = inject(DOCUMENT);

    @Input() data: any;
    @Input() parentId: string;

    tabs: Array<Tab>;
    activeTab: Tab;
    tabIdPrefix = 'tab-panel';
    activeTab$ = new Subject<Tab>();

    constructor(
        protected hashTriggerableService: HashTriggerableService,
        protected route: ActivatedRoute,
        protected idGeneratorPipe: IdGeneratorPipe,
    ) {
        super(hashTriggerableService, route);
    }

    ngOnInit() {
        this.tabs = this.data?.tabs ?? [];
        this.getActiveTab();
    }

    ngAfterViewInit(): void {
        this.setPanelsMinHeights();
    }

    setSelected(id) {
        const nextTab = this.tabs.find(tab => tab.id === id);
        if (!nextTab) {
            return;
        }

        // Unselect the current tab
        this.activeTab.selected = false;
        this.hashTriggerableService.removeHash();

        // Update the selected tab
        this.activeTab = nextTab;
        this.activeTab.selected = true;
        this.hashTriggerableService.updateHash(this.idGeneratorPipe.transform(id, this.tabIdPrefix, this.parentId));

        // Update tab renderer
        this.activeTab$.next(this.activeTab);
    }

    private getActiveTab() {
        // Get tabs id prefix - we have to fudge this and pass a value, but then slice that value off
        let tabsIdPrefix = this.idGeneratorPipe.transform(0, this.tabIdPrefix, this.parentId);
        if (tabsIdPrefix) {
            tabsIdPrefix = tabsIdPrefix.slice(0, -1);
        }

        // Check if hash value is set
        let hashIndex = -1;
        const locationHash = this.route.snapshot?.fragment ?? '';
        if (locationHash.startsWith(tabsIdPrefix)) {
            // We know there is a dash based on the prefix test, and the index number
            // is used to create the tab panel ID, so this is safe
            hashIndex = Number(locationHash.substring(locationHash.lastIndexOf('-'))[1]);
        }

        // Set the active tab
        this.activeTab = this.tabs.find(tab => tab.selected);

        // Update tab renderer
        this.activeTab$.next(this.activeTab);

        // If the hash has a selected tab, override everything
        if (hashIndex !== -1) {
            // Reset all tabs
            this.tabs.forEach(tab => tab.selected = false);

            // Set the active tab
            this.activeTab = this.tabs[hashIndex];
            this.activeTab.selected = true;

            // Set the hash triggerable status to expanded
            super.expand();
        }
    }

    private setPanelsMinHeights(): void {
        const minHeights: {desktop: number; mobile: number} = this.data.panelsMinHeights;

        if (!minHeights.desktop && !minHeights.mobile) {
            return;
        }

        const cssArray: string[] = [];
        const blockId: number = this.data.blockId ?? 0;

        if (minHeights.desktop) {
            cssArray.push(`@media (min-width: ${BREAKPOINTS.tablet}px) {
                    .blockId-${blockId} .tab-block {
                        align-items: flex-start;
                        min-height: ${minHeights.desktop}px;
                    }
                }`
            );
        }

        if (minHeights.mobile) {
            cssArray.push(`@media (max-width: ${BREAKPOINTS.tablet}px) {
                    .blockId-${blockId} .tab-block {
                        align-items: flex-start;
                        min-height: ${minHeights.mobile}px;
                    }
                }`
            );
        }

        // Add css to the page
        this.createStyle(this.document, cssArray.join(''));
    }
}
